import './LeaderboardPP_RVJ.css';
import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react"
import { useLocation } from 'react-router-dom';
import { enrichWithPreviousLeaderboard, fetchUnifiedLeaderboardData, parseExcludedSessionIds } from '../../shared/leaderboardShared';

const Row = React.memo(function Row({ rank, teamname, points, elims, avg_place, wins, games, order, showGamesColumn, onClick, positionChange, refreshDelta, showPositionIndicators, animationEnabled, hasPositionChanged, cascadeFadeEnabled, cascadeIndex, alive, dimmed, showFlags, memberData, isCompactLayout }) {
    const renderPositionChange = () => {

        if (!showPositionIndicators || alive || games < 2 || positionChange === null) {
            return null;
        }

        const getIndicatorStyle = (type, value) => {
            const textLength = String(value).length;
            let baseWidth, fontSize, padding;
            if (textLength === 1) {
                baseWidth = 20;
                fontSize = 11;
                padding = '2px 6px';
            } else if (textLength === 2) {
                baseWidth = 26;
                fontSize = 10;
                padding = '2px 5px';
            } else if (textLength === 3) {
                baseWidth = 32;
                fontSize = 9;
                padding = '2px 4px';
            } else {
                baseWidth = 38;
                fontSize = 8;
                padding = '2px 3px';
            }

            const baseStyle = {
                padding: padding,
                borderRadius: '3px',
                fontSize: `${fontSize}px`,
                fontWeight: 'bold',
                border: '1px solid',
                minWidth: `${baseWidth}px`,
                textAlign: 'center',
                display: 'inline-block',
                marginLeft: '0px',
                position: 'absolute',
                right: isCompactLayout ? '-20px' : '-36px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
            };

            if (type === 'neutral') {
                return {
                    ...baseStyle,
                    backgroundColor: '#666',
                    color: '#fff',
                    borderColor: '#666'
                };
            } else if (type === 'positive') {
                return {
                    ...baseStyle,
                    backgroundColor: '#4CAF50',
                    color: '#fff',
                    borderColor: '#4CAF50'
                };
            } else {
                return {
                    ...baseStyle,
                    backgroundColor: '#f44336',
                    color: '#fff',
                    borderColor: '#f44336'
                };
            }
        };


        if (positionChange === 0) {
            return <span className="position_change neutral" style={getIndicatorStyle('neutral', '=')}>=</span>;
        }
        if (positionChange > 0) {
            return <span className="position_change positive" style={getIndicatorStyle('positive', `+${positionChange}`)}>+{positionChange}</span>;
        } else {
            return <span className="position_change negative" style={getIndicatorStyle('negative', positionChange)}>{positionChange}</span>;
        }
    };

    const getAnimationStyle = () => {
        if (!animationEnabled || !hasPositionChanged || !refreshDelta) return {};

        const rowHeight = 60;
        const realDistance = Math.abs(refreshDelta) * rowHeight;

        const baseSpeed = 200;
        const minDuration = 0.6;
        const maxDuration = 2.0;

        let calculatedDuration = realDistance / baseSpeed;
        calculatedDuration = Math.max(minDuration, Math.min(maxDuration, calculatedDuration));

        const fromPosition = refreshDelta > 0 ? realDistance : -realDistance;

        return {
            '--slide-from': `${fromPosition}px`,
            '--slide-to': '0px',
            animation: `slideFromTo ${calculatedDuration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
            zIndex: 10
        };
    };

    const hasFlagMembers = showFlags && memberData && memberData.length > 0;
    const estimatedLabelLength = hasFlagMembers
        ? memberData.reduce((sum, member) => sum + (member?.name?.length || 0), 0) + ((memberData.length - 1) * 3) + (memberData.length * 3) + (alive ? 2 : 0)
        : teamname.length + (alive ? 2 : 0);

    const getAutoFontSize = (length) => {
        if (length <= 16) return 22;
        if (length <= 20) return 21;
        if (length <= 24) return 20;
        if (length <= 28) return 19;
        if (length <= 32) return 17;
        if (length <= 38) return 16;
        if (length <= 44) return 15;
        if (length <= 52) return 14;
        if (length <= 60) return 13;
        return 12;
    };

    const teamFontSize = getAutoFontSize(estimatedLabelLength);
    const flagSize = Math.max(14, Math.min(23, teamFontSize + 1));
    const rankClass = rank <= 3 ? ` rank_${rank}` : '';
    const pointsDigits = String(points ?? '').length;
    const pointsFontSize = pointsDigits >= 6 ? 17 : pointsDigits >= 5 ? 19 : 23;
    const nameRef = useRef(null);
    const [nameScale, setNameScale] = useState(1);

    useLayoutEffect(() => {
        const element = nameRef.current;
        if (!element || !element.parentElement) return;

        const previous = element.style.transform;
        element.style.transform = 'none';
        const available = element.parentElement.clientWidth - 28 - (alive ? 44 : 0);
        const needed = element.scrollWidth;
        element.style.transform = previous;

        setNameScale(available > 0 && needed > available
            ? Math.max(0.55, available / needed)
            : 1);
    }, [teamname, memberData, showFlags, teamFontSize, alive]);

    return (
        <div className={`row_container${rankClass}${dimmed ? ' is_dead' : ''}`} style={{
            '--animation-order': order,
            opacity: cascadeFadeEnabled ? 0 : (animationEnabled && hasPositionChanged ? 0.9 : 1),
            animation: cascadeFadeEnabled ? 'fadeIn 0.8s forwards' : 'none',
            animationDelay: cascadeFadeEnabled ? `${cascadeIndex * 0.1}s` : '0s',
            transition: animationEnabled && hasPositionChanged ? 'none' : 'opacity 0.3s ease',
            ...getAnimationStyle()
        }}>
            <div className='rank_container'>
                {rank}
                {renderPositionChange()}
            </div>
            <div className='name_container' onClick={onClick}>
                {alive && <span className='alive-dot' />}
                <span
                    ref={nameRef}
                    className='team_name'
                    style={{
                        fontSize: `${teamFontSize}px`,
                        transform: nameScale < 1 ? `scaleX(${nameScale})` : undefined,
                    }}
                >
                    {hasFlagMembers ? (
                        memberData.map((member, idx) => (
                            <span key={idx} className='member_with_flag'>
                                <img
                                    src={member.flagUrl}
                                    alt=""
                                    className='flag_icon'
                                    style={{ width: `${flagSize}px`, height: `${flagSize}px` }}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = member.fallbackFlagUrl;
                                    }}
                                />
                                <span>{member.name}</span>
                                {idx < memberData.length - 1 && <span className='separator'>-</span>}
                            </span>
                        ))
                    ) : (
                        teamname
                    )}
                </span>
            </div>
            <div className='info_box avg_place_box'>{avg_place.toFixed(2)}</div>
            <div className='info_box'>{elims}</div>
            <div className={`info_box wins_box${wins > 0 ? ' has_wins' : ''}`}>{wins}</div>
            <div className='info_box points_box' style={{ fontSize: `${pointsFontSize}px` }}>{points}</div>
            {showGamesColumn && <div className='info_box games_box'>{games}</div>}
        </div>
    );
}, (prevProps, nextProps) => {

    return (
        prevProps.rank === nextProps.rank &&
        prevProps.teamname === nextProps.teamname &&
        prevProps.points === nextProps.points &&
        prevProps.elims === nextProps.elims &&
        Math.abs(prevProps.avg_place - nextProps.avg_place) < 0.01 &&
        prevProps.wins === nextProps.wins &&
        prevProps.games === nextProps.games &&
        prevProps.showGamesColumn === nextProps.showGamesColumn &&
        prevProps.positionChange === nextProps.positionChange &&
        prevProps.refreshDelta === nextProps.refreshDelta &&
        prevProps.showPositionIndicators === nextProps.showPositionIndicators &&
        prevProps.animationEnabled === nextProps.animationEnabled &&
        prevProps.hasPositionChanged === nextProps.hasPositionChanged &&
        prevProps.cascadeFadeEnabled === nextProps.cascadeFadeEnabled &&
        prevProps.alive === nextProps.alive &&
        prevProps.dimmed === nextProps.dimmed &&
        prevProps.showFlags === nextProps.showFlags &&
        prevProps.memberData === nextProps.memberData &&
        prevProps.isCompactLayout === nextProps.isCompactLayout
    );
});

function LeaderboardPP_RVJ() {

    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const leaderboard_id = urlParams.get('id');
    const cascadeParam = urlParams.get('cascade');
    const flagsParam = urlParams.get('flags');
    const themeParam = urlParams.get('theme');
    const theme = ['deep', 'neon', 'clean'].includes(themeParam) ? themeParam : 'deep';
    const titleText = urlParams.get('title') || 'CLASSEMENT';
    const subtitleText = urlParams.get('subtitle') || '';
    const simulateAlive = urlParams.get('simulate_alive') === 'true';
    const simulateAliveCount = Number.parseInt(urlParams.get('simulate_alive_count') || '', 10);
    const excludedSessionIds = useMemo(() => parseExcludedSessionIds(new URLSearchParams(location.search)), [location.search]);
    const excludedSessionIdsKey = useMemo(() => Array.from(excludedSessionIds).sort().join(','), [excludedSessionIds]);

    const [leaderboard, setLeaderboard] = useState([]);
    const [apiPage, setApiPage] = useState(0);
    const [localPage, setLocalPage] = useState(0);
    const [totalApiPages, setTotalApiPages] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFlags, setShowFlags] = useState(flagsParam === 'true');
    const [showSearch, setShowSearch] = useState(false);


    const showGamesColumn = urlParams.get('games') === 'true';
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [teamDetails, setTeamDetails] = useState({});
    const [previousPositions, setPreviousPositions] = useState({});
    const [lastChangeTime, setLastChangeTime] = useState(Date.now());
    const [showPositionIndicators, setShowPositionIndicators] = useState(false);
    const [hasRefreshedOnce, setHasRefreshedOnce] = useState(false);
    const [animationEnabled, setAnimationEnabled] = useState(false);
    const [cascadeFadeEnabled, setCascadeFadeEnabled] = useState(cascadeParam === 'true');
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const previousLeaderboardRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const wasAllDeadRef = useRef(false);


    const loadLeaderboard = async () => {
        try {
            const data = await fetchUnifiedLeaderboardData({
                leaderboardId: leaderboard_id,
                excludedSessionIds,
                showFlags,
                forceRankByPoints: true,
                includeV7: true,
                indicatorsOnlyWhenAllDead: true,
            });

            setTotalApiPages(data.totalPages);
            setShowPositionIndicators(data.showPositionIndicators);
            setHasRefreshedOnce(true);

            const previousData = previousLeaderboardRef.current;
            const merged = enrichWithPreviousLeaderboard(data.leaderboard, previousData);
            const previousPlaces = new Map(
                (previousData || []).map((team) => [team.teamname, team.place])
            );
            const aliveLimit = Number.isFinite(simulateAliveCount) ? simulateAliveCount : 0;
            const withRefreshDeltas = merged.leaderboard.map((team, index) => {
                const previousPlace = previousPlaces.get(team.teamname);
                return {
                    ...team,
                    alive: simulateAlive
                        ? (aliveLimit > 0 ? index < aliveLimit : index % 3 === 0)
                        : team.alive,
                    refreshDelta: typeof previousPlace === 'number' ? previousPlace - team.place : 0,
                };
            });
            setLeaderboard(withRefreshDeltas);
            setTeamDetails(data.teamDetails);
            previousLeaderboardRef.current = withRefreshDeltas;

            if (isInitialLoadRef.current) {
                setIsInitialLoad(false);
                isInitialLoadRef.current = false;
            }

            if (previousData && merged.changedCount > 0) {
                setLastChangeTime(Date.now());
                setAnimationEnabled(true);
                setTimeout(() => { setAnimationEnabled(false); }, 2500);
            } else {
                setAnimationEnabled(false);
            }

            return;
        } catch (error) {
            console.error('Error loading leaderboard data:', error);
        }
    };

    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'F8') {
                event.preventDefault();
                setCascadeFadeEnabled(prev => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    useEffect(() => {
        loadLeaderboard();

        const interval = setInterval(loadLeaderboard, 15000);

        return () => clearInterval(interval);
    }, [leaderboard_id, showFlags, excludedSessionIdsKey]);

    useEffect(() => {
        function handleKeyDown(event) {
            if (event.key === 'F1') {
                event.preventDefault();
                setShowSearch(prev => !prev);
            }
        }

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        setLocalPage(0);
    }, [searchQuery]);



    const handleTeamClick = (teamname) => {
        setSelectedTeam(teamname);
    };

    const closeModal = () => {
        setSelectedTeam(null);
    };



    function nextPage() {
        const total = getFilteredLeaderboard().length;
        const maxPages = Math.max(0, Math.ceil(total / 20) - 1);

        if (localPage < maxPages) {
            setLocalPage(localPage + 1);
        }
    }

    function previousPage() {
        if (localPage > 0) {
            setLocalPage(localPage - 1);
        }
    }

    const getFilteredLeaderboard = () => {
        if (!searchQuery) return leaderboard;
        return leaderboard.filter(team => {
            if (team.teamname.toLowerCase().includes(searchQuery.toLowerCase())) {
                return true;
            }
            if (!isNaN(searchQuery) && searchQuery.trim() !== '') {
                const searchPosition = parseInt(searchQuery.trim());
                if (team.place === searchPosition) {
                    return true;
                }
            }
            if (teamDetails[team.teamname] && teamDetails[team.teamname].members) {
                return teamDetails[team.teamname].members.some(member =>
                    member.ingame_name && member.ingame_name.toLowerCase().includes(searchQuery.toLowerCase())
                );
            }
            return false;
        });
    };

    const filteredLeaderboard = getFilteredLeaderboard();
    const hasAliveTeams = leaderboard.some((team) => team.alive);
    const currentPage = Math.floor(apiPage + localPage);
    const leftItems = filteredLeaderboard.slice(currentPage * 20, (currentPage * 20) + 10);
    const rightItems = filteredLeaderboard.slice((currentPage * 20) + 10, (currentPage * 20) + 20);
    const isSingleColumnLayout = rightItems.length === 0;
    const displayedLeaderboard = leftItems;
    const nextPageFromPoints = nextPage;
    const nextPageFromGames = nextPage;

    const renderHeader = () => (
        <div className='header_container'>
            <div className='rank_header' onClick={previousPage}>PLACE</div>
            <div className='name_header'>ÉQUIPES</div>
            <div className='info_header avg_place_header'>AVG PLACE</div>
            <div className='info_header'>ELIMS</div>
            <div className='info_header'>WINS</div>
            <div className='info_header pts_header' onClick={nextPage}>PTS</div>
            {showGamesColumn && <div onClick={nextPage} className='info_header games_header'>GAMES</div>}
        </div>
    );

    const renderRows = (items, cascadeOffset = 0, isCompactLayout = !isSingleColumnLayout) => items.map((data, index) => {
        const positionChange = Math.abs(data.positionChange || 0);
        let animationOrder;

        if (positionChange >= 500) {
            animationOrder = 1;
        } else if (positionChange >= 100) {
            animationOrder = 2;
        } else if (positionChange >= 50) {
            animationOrder = 3;
        } else if (positionChange >= 10) {
            animationOrder = 4;
        } else if (positionChange > 0) {
            animationOrder = 5;
        } else {
            animationOrder = index + 6;
        }

        return (
            <Row
                key={`${data.teamId || data.teamname}-${data.place}`}
                rank={data.place}
                teamname={data.teamname}
                points={data.points}
                elims={data.elims}
                wins={data.wins}
                games={data.games}
                avg_place={data.avg_place}
                order={animationOrder}
                showGamesColumn={showGamesColumn}
                onClick={() => handleTeamClick(data.teamname)}
                positionChange={data.positionChange}
                refreshDelta={data.refreshDelta || 0}
                showPositionIndicators={showPositionIndicators}
                animationEnabled={animationEnabled && data.hasPositionChanged}
                hasPositionChanged={data.hasPositionChanged || false}
                cascadeFadeEnabled={cascadeFadeEnabled}
                cascadeIndex={cascadeOffset + index}
                alive={data.alive}
                dimmed={hasAliveTeams && !data.alive}
                showFlags={showFlags}
                memberData={data.memberData}
                isCompactLayout={isCompactLayout}
            />
        );
    });

    return (
        <div className='pp_rvj' data-theme={theme} data-games={showGamesColumn ? 'on' : 'off'}>
            <div className='leaderboard_container'>

                {showSearch && (
                    <div className='search_container'>
                        <input
                            type='text'
                            className='search_input'
                            value={searchQuery}
                            placeholder='Rechercher un joueur'
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                )}

                <div className='leaderboard_title'>
                    <span className='title_main'>{titleText}</span>
                    {subtitleText && <span className='title_sub'>{subtitleText}</span>}
                </div>

                <div className={`dual_leaderboard ${isSingleColumnLayout ? 'single-column' : 'two-columns'}`}>
                    <div className='leaderboard_column'>
                        {renderHeader()}
                        {renderRows(leftItems, 0, !isSingleColumnLayout)}
                    </div>
                    {!isSingleColumnLayout && (
                        <div className='leaderboard_column second-column'>
                            {renderHeader()}
                            {renderRows(rightItems, 10, true)}
                        </div>
                    )}
                </div>

                    {selectedTeam && teamDetails[selectedTeam] && (
                        <div className='modal_overlay' onClick={closeModal}>
                            <div className='modal_content' onClick={(e) => e.stopPropagation()}>
                                <div className='modal_header'>
                                    <h2>Stats détaillées - {selectedTeam}</h2>
                                    <button className='close_button' onClick={closeModal}>×</button>
                                </div>
                                <div className='modal_body'>
                                    <div className='team_summary'>
                                        <h3>Résumé de l'équipe :</h3>
                                        <div className='team_stats'>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Position</span>
                                                <span className='stat_value'>#{teamDetails[selectedTeam].teamData.place}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Points actuels</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].teamData.points}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Parties jouées</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.length}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Victoires</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.filter(s => s.place === 1).length}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Top 3</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.filter(s => s.place <= 3).length}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Élims totales</span>
                                                <span className='stat_value'>{teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.kills, 0)}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Place moyenne</span>
                                                <span className='stat_value'>{(teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.place, 0) / teamDetails[selectedTeam].sessions.length).toFixed(2)}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Élims/partie</span>
                                                <span className='stat_value'>{(teamDetails[selectedTeam].sessions.reduce((acc, s) => acc + s.kills, 0) / teamDetails[selectedTeam].sessions.length).toFixed(2)}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Meilleure place</span>
                                                <span className='stat_value'>{Math.min(...teamDetails[selectedTeam].sessions.map(s => s.place))}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Pire place</span>
                                                <span className='stat_value'>{Math.max(...teamDetails[selectedTeam].sessions.map(s => s.place))}</span>
                                            </div>
                                            <div className='stat_item'>
                                                <span className='stat_label'>Max élims/partie</span>
                                                <span className='stat_value'>{Math.max(...teamDetails[selectedTeam].sessions.map(s => s.kills))}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='members_section'>
                                        <h3>Membre(s) de l'équipe :</h3>
                                        <div className='members_list'>
                                            {teamDetails[selectedTeam].members.map((member, index) => (
                                                <div key={index} className='member_item'>
                                                    <span className='member_name'>{member.name}</span>
                                                    {member.ingame && <span className='member_ingame'>{member.ingame}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='sessions_section'>
                                        <h3>Historique détaillé des games :</h3>
                                        <div className='sessions_table'>
                                            <div className='session_header'>
                                                <div>Game</div>
                                                <div>Place</div>
                                                <div>Éliminations</div>
                                            </div>
                                            {teamDetails[selectedTeam].sessions.map((session, index) => (
                                                <div key={index} className='session_row'>
                                                    <div className='session_highlight'>{index + 1}</div>
                                                    <div className={`place_${session.place <= 3 ? 'top' : session.place <= 10 ? 'good' : 'normal'}`}>{session.place}</div>
                                                    <div className='session_highlight'>{session.kills}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
}

export default LeaderboardPP_RVJ
